import ReactMarkdown from 'react-markdown';
import {
  ChangeEventHandler,
  FC,
  useEffect,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { translate } from '@/translation/translate.ts';
import { ConfigField, ConfigSlideId } from '@/eventConfig/types.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { FormGetFieldStatusFn, FormGetFieldValueFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import { useUiEvent } from '@/util/useUiEvent.ts';
import { useFieldValidation } from '../_pipeline/useValidateField';

interface CheckboxProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const CheckboxInput: FC<CheckboxProps> = ({ field, slideId, onChange, getFieldStatus, getFieldValue }) => {
  const fieldStatus = getFieldStatus(field.name);
  const { handleUiEvent } = useUiEvent();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);

  useEffect(() => {
    // Commit each checkbox as value "false" when initially rendered
    const value = fieldStatus?.value?.raw ?? false;
    const fieldValue = { raw: value, normalized: value };
    const { hint, isValid } = validateField(field.validator, fieldValue);
    onChange(field.name, { value: fieldValue, slideId, isValid: isValid, hint: hint });

    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = 'id_field_' + field.name;
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');
  const checkboxes = field.options ?? [];

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
  ];

  const hint = fromTemplate(fieldHint || field.hint || '');

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        {checkboxes.map((checkboxDefinition, idx) => {
          const handleCheckboxLabelActivate = (
            e: ReactMouseEvent<HTMLLabelElement> | ReactKeyboardEvent<HTMLLabelElement>,
            targetCheckboxId: string
          ) => {
            // Keyboard activation: only act on Space/Enter
            if ('key' in e) {
              const key = e.key;
              const isActivation = key === ' ' || key === 'Spacebar' || key === 'Enter' || key === 'NumpadEnter';
              if (!isActivation) {
                return; // Do nothing on Tab/Arrow/etc.
              }
              e.preventDefault();
              const input = document.getElementById(targetCheckboxId) as HTMLInputElement | null;
              input?.click();
              (e.currentTarget as HTMLLabelElement).focus();
              if (checkboxDefinition.click && handleUiEvent) {
                handleUiEvent(checkboxDefinition.click);
              }
              return;
            }

            // Mouse click: treat as activation
            if (checkboxDefinition.click && handleUiEvent) {
              handleUiEvent(checkboxDefinition.click);
            }
          };

          const handleCheckboxChange: ChangeEventHandler<HTMLInputElement> = (e) => {
            if (e.target.checked) {
              const fieldValue = {
                raw: checkboxDefinition.value,
                normalized: checkboxDefinition.value,
              };
              const { hint, isValid } = validateField(field.validator, fieldValue);

              onChange(field.name, { value: fieldValue, hint, isValid, slideId });
            } else {
              const fieldValue = {
                raw: '',
                normalized: '',
              };
              const { hint, isValid } = validateField(field.validator, fieldValue);

              onChange(field.name, { value: fieldValue, hint, isValid, slideId });
            }
          };

          const checkboxId = htmlId + '_' + idx + '_' + checkboxDefinition.name;

          return (
            <div className="select-option" key={checkboxDefinition.name}>
              <input
                type="checkbox"
                value={`${checkboxDefinition.value}`}
                name={'options_' + checkboxDefinition.name}
                id={checkboxId}
                checked={`${checkboxDefinition.value}` == value}
                onChange={handleCheckboxChange}
                tabIndex={-1}
              />

              {checkboxDefinition.label && (
                <label
                  id={`${checkboxId}_label`}
                  htmlFor={checkboxId}
                  className={isRequired ? 'required' : ''}
                  role="checkbox"
                  aria-checked={`${checkboxDefinition.value}` == value}
                  tabIndex={0}
                  onClick={(e) => {
                    handleCheckboxLabelActivate(e, checkboxId);
                    (e.currentTarget as HTMLLabelElement).focus();
                  }}
                  onKeyDown={(e) => {
                    const key = e.key;
                    if (key === ' ' || key === 'Spacebar' || key === 'Enter' || key === 'NumpadEnter') {
                      handleCheckboxLabelActivate(e, checkboxId);
                    }
                  }}
                >
                  <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
                    {translate(checkboxDefinition.label)}
                  </ReactMarkdown>
                </label>
              )}
            </div>
          );
        })}
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
